import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Search, Database, Table2, Columns3, Loader2, ChevronRight, Hash, KeyRound, Type, AlertCircle, Leaf } from 'lucide-react';
import { toast } from 'sonner';

const DbBrowser = () => {
  const [dbType, setDbType] = useState('mysql'); // 'mysql' | 'mongo'
  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);
  const [records, setRecords] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [showColumnsDrawer, setShowColumnsDrawer] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);

  const fetchTables = useCallback(async () => {
    setLoadingTables(true);
    setSelectedTable(null);
    setRecords([]);
    setColumns([]);
    setShowColumnsDrawer(false);
    try {
      if (dbType === 'mysql') {
        const res = await api.get('/mysql-browser/tables');
        const list = res.data.tables.map(t => ({ name: t }));
        setTables(list);
        setFilteredTables(list);
      } else {
        const res = await api.get('/mysql-browser/mongo/collections');
        setTables(res.data.collections);
        setFilteredTables(res.data.collections);
      }
    } catch (e) {
      toast.error(`Failed to load ${dbType === 'mysql' ? 'tables' : 'collections'}`);
    } finally {
      setLoadingTables(false);
    }
  }, [dbType]);

  useEffect(() => { fetchTables(); }, [fetchTables]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTables(tables);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredTables(tables.filter(t => t.name.toLowerCase().includes(q)));
    }
  }, [searchQuery, tables]);

  const handleSelectTable = async (tableName) => {
    setSelectedTable(tableName);
    setRecords([]);
    setColumns([]);
    setShowColumnsDrawer(false);
    setLoadingRecords(true);
    try {
      const url = dbType === 'mysql'
        ? `/mysql-browser/tables/${tableName}/records`
        : `/mysql-browser/mongo/collections/${tableName}/records`;
      const res = await api.get(url);
      setRecords(res.data.records);
    } catch (e) {
      toast.error(`Failed to load records for ${tableName}`);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleShowColumns = async () => {
    if (!selectedTable) return;
    setShowColumnsDrawer(true);
    setLoadingColumns(true);
    try {
      const url = dbType === 'mysql'
        ? `/mysql-browser/tables/${selectedTable}/columns`
        : `/mysql-browser/mongo/collections/${selectedTable}/fields`;
      const res = await api.get(url);
      setColumns(res.data.columns);
    } catch (e) {
      toast.error('Failed to load column metadata');
    } finally {
      setLoadingColumns(false);
    }
  };

  const handleSwitchDb = (type) => {
    if (type === dbType) return;
    setDbType(type);
    setSearchQuery('');
  };

  const columnKeys = records.length > 0 ? Object.keys(records[0]) : [];

  const keyBadge = (key) => {
    if (key === 'PRI') return <Badge className="bg-amber-600/20 text-amber-400 border-amber-600/30 text-[10px] px-1 py-0 rounded-sm font-mono">PK</Badge>;
    if (key === 'UNI') return <Badge className="bg-cyan-600/20 text-cyan-400 border-cyan-600/30 text-[10px] px-1 py-0 rounded-sm font-mono">UQ</Badge>;
    if (key === 'MUL') return <Badge className="bg-violet-600/20 text-violet-400 border-violet-600/30 text-[10px] px-1 py-0 rounded-sm font-mono">IDX</Badge>;
    return null;
  };

  return (
    <div className="flex h-[calc(100vh-140px)] gap-0 border border-border rounded-none overflow-hidden" data-testid="db-browser">
      {/* ── Left Panel: DB Switch + Table List ── */}
      <div className="w-64 min-w-[220px] border-r border-border bg-card flex flex-col" data-testid="db-browser-table-list">
        {/* DB Type Tabs */}
        <div className="flex border-b border-border" data-testid="db-type-tabs">
          <button
            onClick={() => handleSwitchDb('mysql')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold transition-colors
              ${dbType === 'mysql'
                ? 'bg-blue-600/10 text-blue-500 border-b-2 border-blue-500'
                : 'text-muted-foreground hover:bg-muted/50'
              }`}
            data-testid="db-tab-mysql"
          >
            <Database className="h-3.5 w-3.5" />
            MySQL
          </button>
          <button
            onClick={() => handleSwitchDb('mongo')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold transition-colors
              ${dbType === 'mongo'
                ? 'bg-emerald-600/10 text-emerald-500 border-b-2 border-emerald-500'
                : 'text-muted-foreground hover:bg-muted/50'
              }`}
            data-testid="db-tab-mongo"
          >
            <Leaf className="h-3.5 w-3.5" />
            MongoDB
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              className="rounded-none pl-8 h-8 text-sm"
              placeholder={`Filter ${dbType === 'mysql' ? 'tables' : 'collections'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="db-browser-search"
            />
          </div>
          <div className="text-[11px] text-muted-foreground mt-2 tabular-nums">
            {filteredTables.length} / {tables.length} {dbType === 'mysql' ? 'tables' : 'collections'}
          </div>
        </div>

        {/* Table List */}
        <div className="flex-1 overflow-y-auto">
          {loadingTables ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            filteredTables.map((t) => (
              <button
                key={t.name}
                onClick={() => handleSelectTable(t.name)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors border-b border-border/40
                  ${selectedTable === t.name
                    ? dbType === 'mysql' ? 'bg-blue-600/10 text-blue-500 font-medium' : 'bg-emerald-600/10 text-emerald-500 font-medium'
                    : 'text-foreground/80 hover:bg-muted/50'
                  }`}
                data-testid={`db-table-${t.name}`}
              >
                <Table2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate font-mono text-xs">{t.name}</span>
                {dbType === 'mongo' && t.count !== undefined && (
                  <span className="ml-auto text-[10px] text-muted-foreground tabular-nums shrink-0">{t.count.toLocaleString()}</span>
                )}
                {selectedTable === t.name && dbType === 'mysql' && <ChevronRight className="h-3 w-3 ml-auto shrink-0" />}
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Right Panel: Records ── */}
      <div className="flex-1 flex flex-col bg-background min-w-0" data-testid="db-browser-records">
        {!selectedTable ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
            {dbType === 'mysql' ? <Database className="h-10 w-10 opacity-30" /> : <Leaf className="h-10 w-10 opacity-30" />}
            <span className="text-sm">Select a {dbType === 'mysql' ? 'table' : 'collection'} to browse records</span>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
              <Badge variant="outline" className={`rounded-sm text-[10px] px-1.5 py-0 font-mono ${dbType === 'mysql' ? 'border-blue-500/30 text-blue-500' : 'border-emerald-500/30 text-emerald-500'}`}>
                {dbType === 'mysql' ? 'MySQL' : 'Mongo'}
              </Badge>
              <h3 className="text-sm font-bold font-mono tracking-tight" data-testid="db-browser-table-title">
                {selectedTable}
              </h3>
              {!loadingRecords && (
                <Badge variant="outline" className="rounded-sm text-[11px] tabular-nums font-mono">
                  {records.length} rows
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="rounded-none h-7 px-2 ml-auto gap-1.5"
                onClick={handleShowColumns}
                data-testid="db-browser-columns-btn"
              >
                <Columns3 className="h-3.5 w-3.5" />
                <span className="text-xs">Attributes</span>
              </Button>
            </div>

            {/* Data */}
            {loadingRecords ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : records.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2">
                <AlertCircle className="h-6 w-6 opacity-40" />
                <span className="text-sm">No records found</span>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                <table className="w-full text-xs" data-testid="db-browser-data-table">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-muted/80 backdrop-blur-sm border-b border-border">
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground text-[11px] tracking-wider border-r border-border/40 whitespace-nowrap w-8">#</th>
                      {columnKeys.map((col) => (
                        <th key={col} className="text-left px-3 py-2 font-semibold text-muted-foreground text-[11px] tracking-wider border-r border-border/40 whitespace-nowrap font-mono">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((row, i) => (
                      <tr key={i} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
                        <td className="px-3 py-1.5 text-muted-foreground tabular-nums border-r border-border/40">{i + 1}</td>
                        {columnKeys.map((col) => {
                          const val = row[col];
                          const display = val === null ? 'NULL' : String(val);
                          const isNull = val === null;
                          return (
                            <td
                              key={col}
                              className={`px-3 py-1.5 whitespace-nowrap border-r border-border/40 max-w-[300px] truncate font-mono ${isNull ? 'text-muted-foreground/50 italic' : ''}`}
                              title={display}
                            >
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Columns/Fields Drawer (from left) ── */}
      <Sheet open={showColumnsDrawer} onOpenChange={setShowColumnsDrawer}>
        <SheetContent side="left" className="w-[420px] sm:w-[460px] p-0 rounded-none" data-testid="db-browser-columns-drawer">
          <SheetHeader className="px-5 py-4 border-b border-border">
            <SheetTitle className="text-sm font-bold font-mono flex items-center gap-2">
              <Columns3 className="h-4 w-4" />
              {selectedTable} — {dbType === 'mysql' ? 'Columns' : 'Fields'}
            </SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto h-[calc(100%-60px)]">
            {loadingColumns ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="divide-y divide-border">
                {columns.map((col, idx) => (
                  <div key={idx} className="px-5 py-3 hover:bg-muted/30 transition-colors" data-testid={`column-${col.field}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm font-semibold">{col.field}</span>
                      {keyBadge(col.key)}
                      {col.extra === 'auto_increment' && (
                        <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/30 text-[10px] px-1 py-0 rounded-sm font-mono">AI</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Type className="h-3 w-3" />{col.type}</span>
                      <span className="flex items-center gap-1"><Hash className="h-3 w-3" />Null: {col.null}</span>
                      {col.default !== null && (
                        <span className="flex items-center gap-1"><KeyRound className="h-3 w-3" />Default: {String(col.default)}</span>
                      )}
                    </div>
                    {col.sample && dbType === 'mongo' && (
                      <div className="text-[11px] text-muted-foreground/60 mt-1 font-mono truncate max-w-[380px]" title={col.sample}>
                        Sample: {col.sample}
                      </div>
                    )}
                    {col.comment && (
                      <div className="text-[11px] text-muted-foreground/70 mt-1 italic">{col.comment}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default DbBrowser;
